import { FC, useEffect, useMemo, useRef } from 'react';
import { useBotContext } from '../useBotContext';
import WithContext from '../molecule/WithContext';
import { BOT_FORM_STORAGE_KEY, createBotInitialData, isGeneratedTag } from '../../helpers/data/bot';
import { IBotData, IChildren } from '../../models';

const BotStorageSync: FC = () => {
  const { value, setValue } = useBotContext();
  const originalTagRef = useRef<string | null>(null);
  const isUserTypedTagRef = useRef<boolean>(false);
  const previousTagRef = useRef<string | null>(null);

  useEffect(() => {
    try {
      const raw =
        typeof window !== 'undefined'
          ? window.localStorage.getItem(BOT_FORM_STORAGE_KEY)
          : null;

      if (!raw) {
        // No stored data - originalTagRef will be set from initialValue in second useEffect
        // Tag will be generated, so it's not user-typed
        isUserTypedTagRef.current = false;
        return;
      }

      const parsed = JSON.parse(raw) as IBotData;
      if (
        parsed &&
        typeof parsed === 'object' &&
        Array.isArray(parsed.tunnels)
      ) {
        // If tag is not in storage (user never changed it), generate one
        if (!parsed.tag) {
          const initialData = createBotInitialData();
          parsed.tag = initialData.tag;
          originalTagRef.current = initialData.tag;
          // Generated tag, not user-typed
          isUserTypedTagRef.current = false;
        } else {
          // Tag exists in storage (user typed it before)
          // Store the loaded tag as original for comparison
          originalTagRef.current = parsed.tag;
          // Tag was saved, so it's user-typed
          isUserTypedTagRef.current = true;
        }
        previousTagRef.current = parsed.tag || null;
        setValue(parsed);
      }
    } catch {
      // ignore malformed storage - originalTagRef will be set from initialValue
      isUserTypedTagRef.current = false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

useEffect(() => {
  try {
    if (typeof window === 'undefined') return;

    let dataToSave: Partial<IBotData>;

    if (isGeneratedTag(value.tag)) {
      // ❌ auto-generated → never persist
      const { tag, ...rest } = value;
      dataToSave = rest;
    } else {
      // ✅ user-edited → persist tag
      dataToSave = { ...value };
    }

    window.localStorage.setItem(
      BOT_FORM_STORAGE_KEY,
      JSON.stringify(dataToSave)
    );
  } catch {
    // ignore quota / security errors
  }
}, [value]);


  return null;
};
const BotContext: FC<IChildren> = ({ children }) => {
  const initialValue = useMemo<IBotData>(() => createBotInitialData(), []);
  return (
    <WithContext<IBotData> initialValue={initialValue}>
      <BotStorageSync />
      <div className="d-flex w-100 h-100 bot-info__wrapper">{children}</div>
    </WithContext>
  );
};
export default BotContext;
